import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdArrowBack, MdChevronRight } from "react-icons/md";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "../PageLayoutWithHeaderNav";
import { getEmployeeEngagement } from "../../services/managerEngagement";
import EmployeeCoachingPerformance from "./EmployeeCoachingPerformance";
import ReportingPeriodControl from "./ReportingPeriodControl";
import { normalizeReportingPeriodId } from "./reportingPeriod";

const STATUS_COLORS = { ready: "green", transcript_only: "purple", processing: "orange", failed: "red", empty: "gray" };

function duration(value) {
  const total = Math.max(0, Math.round(Number(value || 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function sessionTime(value) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Unknown time" : new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(parsed);
}

function pluralized(count, noun) {
  const value = Number(count || 0);
  return `${value} ${noun}${value === 1 ? "" : "s"}`;
}

function calendarDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function isoDate(value) {
  return value.toISOString().slice(0, 10);
}

function dayLabel(value) {
  const parsed = calendarDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

function shortDate(value) {
  const parsed = calendarDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

export default function EmployeeEngagementPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodId = normalizeReportingPeriodId(searchParams.get("period"));
  const selectedDate = searchParams.get("date");
  const jobClass = searchParams.get("job_class") || undefined;
  const frameworkFamily = searchParams.get("framework") || undefined;
  const reportingGroupId = searchParams.get("reporting_group") || undefined;
  const locationId = searchParams.get("location") || undefined;
  const [data, setData] = useState(null);
  const [threads, setThreads] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [paginationMessage, setPaginationMessage] = useState("");
  const [error, setError] = useState("");
  const restoreAttemptedCursorsRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    restoreAttemptedCursorsRef.current.clear();
    try {
      const response = await getEmployeeEngagement(employeeId, { periodId, day: selectedDate, reportingGroupId, locationId });
      setData(response);
      setThreads(response.threads || response.sessions || []);
      setCursor(response.next_cursor || null);
      setPaginationMessage("");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load advisor activity.");
    } finally {
      setLoading(false);
    }
  }, [employeeId, locationId, periodId, reportingGroupId, selectedDate]);

  useEffect(() => { load(); }, [load]);

  const loadMore = useCallback(async ({ announce = true } = {}) => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const response = await getEmployeeEngagement(employeeId, { periodId, day: selectedDate, cursor, reportingGroupId, locationId });
      const received = response.threads || response.sessions || [];
      const seen = new Set(threads.map((item) => item.id));
      const additions = received.filter((item) => !seen.has(item.id));
      const shown = threads.length + additions.length;
      setThreads([...threads, ...additions]);
      if (announce) {
        setPaginationMessage(
          additions.length
            ? `Loaded ${pluralized(additions.length, "additional customer conversation")}. ${shown} of ${response.summary?.thread_count ?? data?.summary?.thread_count ?? shown} shown for this reporting period.`
            : "No additional customer conversations were found in this reporting period."
        );
      }
      setCursor(response.next_cursor || null);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load additional customer conversations.");
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, data?.summary?.thread_count, employeeId, locationId, periodId, reportingGroupId, selectedDate, threads]);

  useEffect(() => {
    if (!data || !location.hash) return undefined;
    const anchorId = decodeURIComponent(location.hash.replace(/^#/, ""));
    const target = document.getElementById(anchorId);
    if (!target && anchorId.startsWith("customer-conversation-") && cursor && !loadingMore) {
      if (!restoreAttemptedCursorsRef.current.has(cursor)) {
        restoreAttemptedCursorsRef.current.add(cursor);
        loadMore({ announce: false });
      }
      return undefined;
    }
    if (!target) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: anchorId === "customer-conversations" ? "start" : "center" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [cursor, data, loadMore, loadingMore, location.hash, threads]);

  const weeks = useMemo(() => {
    if (!data?.period?.start_date || !data?.period?.end_date) return [];
    const conversationsByDay = new Map();
    for (const thread of threads) {
      const date = thread.local_date || String(thread.last_activity_at || thread.created_at || "").slice(0, 10) || "Unknown";
      if (selectedDate && date !== selectedDate) continue;
      if (!conversationsByDay.has(date)) conversationsByDay.set(date, []);
      conversationsByDay.get(date).push(thread);
    }
    const start = calendarDate(data.period.start_date);
    const end = calendarDate(data.period.end_date);
    if (!start || !end) return [];
    const days = [];
    for (let current = start; current <= end; current = new Date(current.getTime() + 86400000)) {
      const date = isoDate(current);
      days.push({ date, conversations: conversationsByDay.get(date) || [] });
    }
    const result = [];
    for (let index = 0; index < days.length; index += 7) {
      const weekDays = days.slice(index, index + 7);
      result.push({
        start: weekDays[0]?.date,
        end: weekDays[weekDays.length - 1]?.date,
        days: weekDays,
        count: weekDays.reduce((total, day) => total + day.conversations.length, 0),
      });
    }
    return result.reverse();
  }, [data?.period, threads, selectedDate]);
  const selectedWeekIndex = selectedDate
    ? weeks.findIndex((week) => week.days.some((day) => day.date === selectedDate))
    : -1;
  const openWeekParam = searchParams.get("open_week");
  const openDayParam = searchParams.get("open_day");
  const openWeekStart = openWeekParam === "none"
    ? null
    : openWeekParam || (selectedWeekIndex >= 0 ? weeks[selectedWeekIndex]?.start : null);
  const openWeekIndex = openWeekStart
    ? weeks.findIndex((week) => week.start === openWeekStart)
    : -1;
  const openDay = openDayParam === "none" ? null : openDayParam || selectedDate;

  const setExpansion = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value || "none");
    setSearchParams(next, { replace: true });
  };
  const teamParams = new URLSearchParams();
  if (periodId) teamParams.set("period", periodId);
  if (jobClass) teamParams.set("job_class", jobClass);
  if (frameworkFamily) teamParams.set("framework", frameworkFamily);
  if (reportingGroupId) teamParams.set("reporting_group", reportingGroupId);
  if (locationId) teamParams.set("location", locationId);
  const teamUrl = `/manager/engagement${teamParams.toString() ? `?${teamParams.toString()}` : ""}`;

  const threadUrl = (threadId, returnAnchor) => {
    const params = new URLSearchParams();
    if (data?.period?.id) params.set("period", data.period.id);
    if (jobClass) params.set("job_class", jobClass);
    if (frameworkFamily) params.set("framework", frameworkFamily);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    if (openWeekStart) params.set("open_week", openWeekStart);
    if (openDay) params.set("open_day", openDay);
    if (returnAnchor) params.set("return_anchor", returnAnchor);
    return `/manager/employees/${employeeId}/threads/${threadId}?${params.toString()}`;
  };
  const openThread = (threadId) => {
    const returnAnchor = `customer-conversation-${threadId}`;
    navigate(`${location.pathname}${location.search}#${returnAnchor}`, { replace: true });
    navigate(threadUrl(threadId, returnAnchor));
  };
  const changePeriod = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("period", value);
    next.delete("date");
    setSearchParams(next, { replace: true });
  };

  return (
    <PageLayoutWithHeaderNav maxWidth="container.xl">
      <VStack align="stretch" spacing={6}>
        <Button alignSelf="flex-start" variant="ghost" size="sm" leftIcon={<MdArrowBack />} onClick={() => navigate(teamUrl)}>Employees</Button>
        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
        {loading ? <Flex justify="center" py={20}><Spinner /></Flex> : data ? (
          <Grid templateColumns={{ base: "1fr", lg: "280px minmax(0, 1fr)" }} gap={5} alignItems="start">
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5} position={{ lg: "sticky" }} top={{ lg: 8 }}>
              <VStack spacing={3} textAlign="center">
                <Avatar size="xl" name={data.employee.name} bg="blue.100" color="blue.700" />
                <Box><Heading size="md">{data.employee.name}</Heading><Text mt={1} color="gray.500" fontSize="sm">{jobClass?.replace("_", " ") || "Team member"}</Text></Box>
              </VStack>
              <Box borderTopWidth="1px" borderColor="gray.200" mt={5} pt={4}>
                <ReportingPeriodControl period={data.period} periodId={periodId} onChange={changePeriod} compact />
                {selectedDate && <Text mt={2} fontSize="xs" color="blue.600">Showing activity selected for {dayLabel(selectedDate)}</Text>}
              </Box>
              <Grid templateColumns="repeat(2, 1fr)" gap={3} mt={4}>
                <Box bg="gray.50" borderRadius="lg" p={3}><Text fontSize="xl" fontWeight="bold">{data.summary.thread_count ?? data.summary.recording_count}</Text><Text fontSize="xs" color="gray.500">Customer conversations</Text></Box>
                <Box bg="gray.50" borderRadius="lg" p={3}><Text fontSize="xl" fontWeight="bold">{data.summary.ready}</Text><Text fontSize="xs" color="gray.500">Ready</Text></Box>
              </Grid>
              <HStack mt={4} spacing={2} wrap="wrap">
                {data.summary.processing > 0 && <Badge colorScheme="orange">{data.summary.processing} processing</Badge>}
                {data.summary.failed > 0 && <Badge colorScheme="red">{data.summary.failed} failed</Badge>}
              </HStack>
            </Box>

            <VStack align="stretch" spacing={6}>
              <EmployeeCoachingPerformance
                employeeId={employeeId}
                periodId={periodId}
                jobClass={jobClass}
                frameworkFamily={frameworkFamily}
                reportingGroupId={reportingGroupId}
                locationId={locationId}
              />

              <Box id="customer-conversations" scrollMarginTop="80px" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={{ base: 4, md: 5 }}>
                <Box mb={4}><Heading size="md">Customer Conversations</Heading><Text mt={1} fontSize="sm" color="gray.500">Conversations are limited to the reporting period shown on this page. Expand a week and day to review a coaching report and cleaned transcript.</Text></Box>
                <VStack align="stretch" spacing={5}>
                  <Accordion allowToggle index={openWeekIndex} onChange={(index) => {
                    const week = Number.isInteger(index) && index >= 0 ? weeks[index] : null;
                    const next = new URLSearchParams(searchParams);
                    next.set("open_week", week?.start || "none");
                    if (!week || (openDay && !week.days.some((day) => day.date === openDay))) next.set("open_day", "none");
                    setSearchParams(next, { replace: true });
                  }}>
                    {weeks.map((week) => (
                      <AccordionItem key={week.start} borderWidth="1px" borderColor="gray.200" borderRadius="lg" mb={3} overflow="hidden">
                        <AccordionButton py={4} _expanded={{ bg: "blue.50" }}>
                          <Box flex="1" textAlign="left">
                            <Text fontWeight="bold">Week of {shortDate(week.start)}</Text>
                            <Text mt={0.5} fontSize="xs" color="gray.500">{shortDate(week.start)} – {shortDate(week.end)} · {pluralized(week.count, "customer conversation")}</Text>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel p={3} bg="gray.50">
                          <Accordion allowToggle index={openDay ? week.days.findIndex((day) => day.date === openDay) : -1} onChange={(index) => {
                            const day = Number.isInteger(index) && index >= 0 ? week.days[index] : null;
                            setExpansion("open_day", day?.date || null);
                          }}>
                            {week.days.map((day) => (
                              <AccordionItem key={day.date} borderWidth="0" borderBottomWidth="1px" borderColor="gray.200" _last={{ borderBottomWidth: 0 }}>
                                <AccordionButton px={2} py={3}>
                                  <Text flex="1" textAlign="left" fontWeight="semibold">{dayLabel(day.date)}</Text>
                                  <Text mr={3} fontSize="xs" color="gray.500">{pluralized(day.conversations.length, "conversation")}</Text>
                                  <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel px={2} pb={3}>
                                  <VStack align="stretch" spacing={2}>
                                    {day.conversations.map((thread) => (
                                      <Flex id={`customer-conversation-${thread.id}`} key={thread.id} as="button" type="button" onClick={() => openThread(thread.id)} scrollMarginTop="96px" textAlign="left" align="center" gap={4} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={4} py={3} _hover={{ borderColor: "blue.300", bg: "blue.50" }}>
                                        <Box minW="90px"><Text fontWeight="bold">{sessionTime(thread.last_activity_at || thread.created_at)}</Text><Text fontSize="xs" color="gray.500">{duration(thread.talk_time_ms)}</Text></Box>
                                        <Box flex="1" minW={0}><Text fontWeight="semibold" noOfLines={1}>{thread.title || "Untitled conversation"}</Text><Text fontSize="xs" color="gray.500">{pluralized(thread.recording_count, "recording")}</Text></Box>
                                        <Badge colorScheme={STATUS_COLORS[thread.status] || "gray"}>{thread.status.replace("_", " ")}</Badge>
                                        <MdChevronRight />
                                      </Flex>
                                    ))}
                                    {!day.conversations.length && <Text px={2} py={2} color="gray.500" fontSize="sm">No customer conversations.</Text>}
                                  </VStack>
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {!weeks.length && <Box p={8} bg="gray.50" borderRadius="lg" textAlign="center"><Text color="gray.500">No customer conversations in this view.</Text></Box>}
                  {paginationMessage && <Text textAlign="center" fontSize="sm" color="gray.600" role="status">{paginationMessage}</Text>}
                  {cursor && <Button alignSelf="center" variant="outline" onClick={() => loadMore()} isLoading={loadingMore}>Load more from this reporting period</Button>}
                </VStack>
              </Box>
            </VStack>
          </Grid>
        ) : null}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
}
